import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { applyOptiBoardFetchScope, boundOptiBoardArchive, groupOptiBoardRows, POSTGREST_DEFAULT_ROW_CAP, trimOptiBoardArchive } from '@/utils/optiBoardTabPredicates';

interface Technician {
  id: string;
  name: string;
  is_present: boolean;
}

export function useOptiboardTickets(shopId: string, shopTimezone: string) {
  const [estimates, setEstimates] = useState<any[]>([]);
  const [scheduled, setScheduled] = useState<any[]>([]);
  const [wip, setWip] = useState<any[]>([]);
  const [completed, setCompleted] = useState<any[]>([]);
  const [pickedUp, setPickedUp] = useState<any[]>([]);
  const [deleted, setDeleted] = useState<any[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [archiveTruncated, setArchiveTruncated] = useState({ pickedUp: false, deleted: false });
  const [liveMayBeTruncated, setLiveMayBeTruncated] = useState(false);

  const fetchTickets = useCallback(async () => {
    if (!shopId) {
      setIsLoading(false);
      return;
    }

    try {
      const baseSelect = `
        *,
        vehicle:vehicles(id, year, make, model, engine_size),
        technician:technicians!tickets_assigned_tech_id_fkey(id, name),
        bay:shop_bays(id, name),
        ticket_jobs(
          job_id,
          minutes_override,
          job:jobs!ticket_jobs_job_id_fkey(
            id,
            name,
            standard_minutes,
            catalog_job_id,
            jobs_catalog!jobs_catalog_job_id_fkey(
              label,
              minutes_default
            )
          )
        )
      `;

      // fix/foh-optiboard-calendar-truth: three DISJOINT downloads (live /
      // picked up / deleted), then every row is classified by the canonical
      // production state — the tab is never decided by a second, SQL-shaped
      // copy of the lifecycle (which dropped overdue scheduled work at midnight).
      // Core-100 R2 CORE-AS-GAP-003: the two archive scopes are bounded to the most recent
      // window (+1 to detect that older rows exist).
      const scoped = (scope: 'Live' | 'PickedUp' | 'Deleted') =>
        boundOptiBoardArchive(
          applyOptiBoardFetchScope(supabase.from('tickets').select(baseSelect).eq('shop_id', shopId), scope),
          scope,
        );

      const [
        liveRes,
        pickedUpRes,
        deletedRes,
        techsRes,
        parkingSpotsRes
      ] = await Promise.all([
        scoped('Live'),
        scoped('PickedUp'),
        scoped('Deleted'),

        // Technicians
        supabase
          .from('technicians')
          .select('id, name, is_present')
          .eq('shop_id', shopId)
          .eq('employment_status', 'Active'),

        // Parking spots for label lookup
        supabase
          .from('parking_lot_spots')
          .select('id, label')
          .eq('shop_id', shopId)
      ]);

      const spots = parkingSpotsRes.data || [];
      const enrichWithSpot = (tickets: any[]) =>
        tickets.map(t => {
          if (!t.parking_spot_id) return { ...t, parking_spot: null };
          const spot = spots.find((s: any) => s.id === t.parking_spot_id);
          return { ...t, parking_spot: spot ? { id: spot.id, label: spot.label } : null };
        });

      for (const r of [liveRes, pickedUpRes, deletedRes]) {
        if (r.error) console.error('Error fetching optiboard tickets:', r.error);
      }
      const pickedUpWindow = trimOptiBoardArchive(pickedUpRes.data || []);
      const deletedWindow = trimOptiBoardArchive(deletedRes.data || []);
      setArchiveTruncated({ pickedUp: pickedUpWindow.truncated, deleted: deletedWindow.truncated });
      setLiveMayBeTruncated((liveRes.data || []).length >= POSTGREST_DEFAULT_ROW_CAP);
      const tabs = groupOptiBoardRows(enrichWithSpot([
        ...(liveRes.data || []), ...pickedUpWindow.rows, ...deletedWindow.rows,
      ]));
      setEstimates(tabs.Estimates);
      setScheduled(tabs.Scheduled);
      setWip(tabs.WIP);
      setCompleted(tabs.Completed);
      setPickedUp(tabs.PickedUp);
      setDeleted(tabs.Deleted);
      setTechnicians((techsRes.data || []) as Technician[]);
    } catch (error) {
      console.error('Error fetching optiboard tickets:', error);
    } finally {
      setIsLoading(false);
    }
  }, [shopId, shopTimezone]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  return {
    estimates,
    scheduled,
    wip,
    completed,
    pickedUp,
    deleted,
    technicians,
    isLoading,
    archiveTruncated,
    liveMayBeTruncated,
    refetch: fetchTickets,
  };
}
